import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { useBrand } from '../context/BrandContext'
import { useNavigate } from 'react-router-dom'
import { colors, s, ui } from '../styles/theme'

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000'

function Segments() {
  const { selectedBrand } = useBrand()
  const [rules, setRules] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null)
  const [saved, setSaved] = useState(false)
  const navigate = useNavigate()

  function fetchRules() {
    if (!selectedBrand) return
    const token = localStorage.getItem('adminToken')
    axios.get(`${API}/admin/brands/${selectedBrand.id}/segments`, {
      headers: { Authorization: token }
    }).then(res => {
      setRules(res.data)
      setLoading(false)
    }).catch(() => setLoading(false))
  }

  useEffect(() => { fetchRules() }, [selectedBrand?.id]) // eslint-disable-line

  async function handleSave(rule) {
    const token = localStorage.getItem('adminToken')
    await axios.put(`${API}/admin/segments/${rule.id}`, rule, {
      headers: { Authorization: token }
    })
    setSaved(true)
    setTimeout(() => setSaved(false), 3000)
    setEditing(null)
    fetchRules()
  }

  async function handleDelete(ruleId) {
    if (!window.confirm('Bu kuralı silmek istediğinize emin misiniz?')) return
    const token = localStorage.getItem('adminToken')
    await axios.delete(`${API}/admin/segments/${ruleId}`, {
      headers: { Authorization: token }
    })
    fetchRules()
  }

  async function handleAdd() {
    const token = localStorage.getItem('adminToken')
    const res = await axios.post(`${API}/admin/brands/${selectedBrand.id}/segments`, {
      name: 'Yeni Kural',
      match_category: true,
      price_range_percent: 30,
      match_stock: true,
      limit_count: 8,
      is_active: true
    }, { headers: { Authorization: token } })
    fetchRules()
    setEditing(res.data.id)
  }

  if (!selectedBrand) return (
    <div style={{ textAlign: 'center', padding: '60px 16px', maxWidth: '100%' }}>
      <p style={{ ...ui.notice, display: 'inline-block', marginBottom: '16px' }}>Önce bir marka seçin.</p>
      <button onClick={() => navigate('/brands')} style={s.btnPrimary}>Markalara Git</button>
    </div>
  )

  if (loading) return <p style={ui.emptyText}>Yükleniyor...</p>

  return (
    <div>
      <div className="sx-wrap" style={{ ...s.flexBetween, marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <h2 style={{ ...s.pageTitle, minWidth: 0 }}>Smart Recommendation — {selectedBrand.name}</h2>
        <button onClick={handleAdd} style={s.btnSecondary}>+ Yeni Kural</button>
      </div>
      {saved && <p style={{ ...ui.notice, display: 'inline-block', marginBottom: '16px' }}>Kaydedildi ✓</p>}
      {rules.map(rule => (
        <div key={rule.id} style={{ ...s.card, padding: '24px' }}>
          {editing === rule.id
            ? <EditRule rule={rule} brandId={selectedBrand.id} onSave={handleSave} onCancel={() => setEditing(null)} />
            : <ViewRule rule={rule} onEdit={() => setEditing(rule.id)} onDelete={() => handleDelete(rule.id)} />}
        </div>
      ))}
    </div>
  )
}

function ViewRule({ rule, onEdit, onDelete }) {
  const ruleTypeLabels = {
    similar: 'Benzer Ürünler',
    cross_category: 'Kategori Çapraz Eşleştirme',
    price_alternative: 'Yakın Fiyatlı Alternatif',
    variant: 'Varyant Alternatifi',
    brand_match: 'Marka Eşleştirme',
    model_match: 'Model Eşleştirme',
    filter_match: 'Filtre Eşleştirme',
    category_bestseller: 'Kategorinin Çok Satanları (liste sayfası)'
  }

  return (
    <div className="sx-wrap" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
      <div style={{ minWidth: 0, flex: '1 1 240px' }}>
        <p className="sx-break" style={{ ...s.sectionTitle, marginBottom: '8px' }}>{rule.name}</p>
        <div className="sx-break" style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          <span style={ui.chip}>Tip: {ruleTypeLabels[rule.rule_type] || rule.rule_type || 'similar'}</span>
          {rule.price_range_percent > 0 && <span style={ui.chip}>Fiyat: ±%{rule.price_range_percent}</span>}
          <span style={ui.chip}>Stokta: {rule.match_stock ? '✓' : '✗'}</span>
          <span style={ui.chip}>Limit: {rule.limit_count}</span>
          {rule.source_category_ids && <span style={{ ...ui.chip, whiteSpace: 'normal', minWidth: 0, maxWidth: '100%' }}>Kaynak Kat: {rule.source_category_ids}</span>}
          {rule.filter_group_code && <span style={{ ...ui.chip, whiteSpace: 'normal', minWidth: 0, maxWidth: '100%' }}>Filtre Grubu: {rule.filter_group_code}</span>}
          {rule.filter_option_code && <span style={{ ...ui.chip, whiteSpace: 'normal', minWidth: 0, maxWidth: '100%' }}>Filtre Değeri: {rule.filter_option_code}</span>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
        <span style={ui.status(rule.is_active)}>
          <span style={ui.statusDot(rule.is_active)} />
          {rule.is_active ? 'Aktif' : 'Pasif'}
        </span>
        <button onClick={onEdit} style={ui.btnSm}>Düzenle</button>
        <button onClick={onDelete} style={ui.btnDangerQuiet}>Sil</button>
      </div>
    </div>
  )
}

function SourceCategoryInput({ form, setForm }) {
  return (
    <div style={{ marginBottom: '12px' }}>
      <label style={s.labelNormal}>
        Kaynak Kategoriler
        <span style={{ color: colors.textMuted, fontSize: '12px', marginLeft: '8px', fontWeight: '400' }}>
          (opsiyonel — boş bırakılırsa tüm kategorilerde çalışır)
        </span>
      </label>
      <p style={{ fontSize: '12px', color: colors.textSecondary, marginBottom: '8px', marginTop: 0 }}>
        {form.rule_type === 'category_bestseller'
          ? 'Carousel bu kategorilerin liste sayfasında görünür. Kategori kodlarını virgülle ayırın; alt kategoriler de taranır.'
          : 'Bu kategorideki ürün sayfasında kural çalışır. Virgülle ayırın.'}
      </p>
      <input
        value={form.source_category_ids || ''}
        onChange={e => setForm({ ...form, source_category_ids: e.target.value })}
        placeholder="T106 veya Göğüs Pompaları >"
        style={s.input} />
    </div>
  )
}

function EditRule({ rule, brandId, onSave, onCancel }) {
  const [form, setForm] = useState({ ...rule })
  const [filterGroups, setFilterGroups] = useState([])
  const [filterOptions, setFilterOptions] = useState([])
  const ruleType = form.rule_type || 'similar'

  useEffect(() => {
    if (ruleType === 'filter_match') {
      const token = localStorage.getItem('adminToken')
      axios.get(`${API}/admin/brands/${brandId}/filter-groups`, {
        headers: { Authorization: token }
      }).then(res => setFilterGroups(res.data || [])).catch(() => {})
    }
  }, [ruleType, brandId])

  useEffect(() => {
    if (ruleType === 'filter_match' && form.filter_group_code) {
      const token = localStorage.getItem('adminToken')
      axios.get(`${API}/admin/brands/${brandId}/filter-options/${form.filter_group_code}`, {
        headers: { Authorization: token }
      }).then(res => setFilterOptions(res.data || [])).catch(() => {})
    } else {
      setFilterOptions([])
    }
  }, [form.filter_group_code, ruleType, brandId])

  const hasPriceRange = ['similar', 'price_alternative', 'brand_match', 'model_match', 'filter_match'].includes(ruleType)
  const hasLimit = true

  return (
    <div>
      <div style={{ marginBottom: '16px' }}>
        <label style={s.labelNormal}>Kural Adı</label>
        <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
          style={s.input} />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={s.labelNormal}>Kural Tipi</label>
        <select value={ruleType} onChange={e => setForm({ ...form, rule_type: e.target.value, filter_group_code: '', filter_option_code: '' })}
          style={s.select}>
          <option value="similar">Benzer Ürünler</option>
          <option value="cross_category">Kategori Çapraz Eşleştirme</option>
          <option value="price_alternative">Yakın Fiyatlı Alternatif</option>
          <option value="variant">Varyant Alternatifi</option>
          <option value="brand_match">Marka Eşleştirme</option>
          <option value="model_match">Model Eşleştirme</option>
          <option value="filter_match">Filtre Eşleştirme</option>
          <option value="category_bestseller">Kategorinin Çok Satanları (liste sayfası)</option>
        </select>
      </div>

      {/* similar, cross_category, price_alternative, variant için kaynak kategori */}
      {!['brand_match', 'model_match', 'filter_match'].includes(ruleType) && (
        <div style={{ ...s.cardFlat, marginBottom: '16px' }}>
          <SourceCategoryInput form={form} setForm={setForm} />
          {ruleType === 'cross_category' && (
            <div style={{ marginTop: '4px' }}>
              <label style={s.labelNormal}>Hedef Kategoriler</label>
              <p style={{ fontSize: '12px', color: colors.textSecondary, marginBottom: '8px', marginTop: 0 }}>
                Bu kategorideki ürünler önerilecek. Virgülle ayırın.
              </p>
              <input
                value={form.cross_category_ids || ''}
                onChange={e => setForm({ ...form, cross_category_ids: e.target.value })}
                placeholder="T395 veya Bakım Ürünleri >"
                style={s.input} />
            </div>
          )}
        </div>
      )}

      {/* Marka eşleştirme */}
      {ruleType === 'brand_match' && (
        <div style={{ ...s.cardFlat, marginBottom: '16px' }}>
          <SourceCategoryInput form={form} setForm={setForm} />
          <div style={{ ...ui.notice, background: colors.bgCard }}>
            Ziyaret edilen ürünün markasıyla aynı markaya sahip diğer ürünler önerilir.
          </div>
        </div>
      )}

      {/* Model eşleştirme */}
      {ruleType === 'model_match' && (
        <div style={{ ...s.cardFlat, marginBottom: '16px' }}>
          <SourceCategoryInput form={form} setForm={setForm} />
          <div style={{ ...ui.notice, background: colors.bgCard }}>
            Ziyaret edilen ürünün modeliyle aynı modele sahip diğer ürünler önerilir.
          </div>
        </div>
      )}

      {ruleType === 'category_bestseller' && (
        <div style={{ ...s.cardFlat, marginBottom: '16px' }}>
          <SourceCategoryInput form={form} setForm={setForm} />
          <div style={{ ...ui.notice, background: colors.bgCard }}>
            Kategori liste sayfasına, o kategorinin (alt kategorileri dahil) son bir yılda en çok satan ürünlerinden bir carousel basar.
            Setler, stokta olmayan ürünler ve görseli olmayanlar listeye girmez. Carousel başlığı yukarıdaki kural adıdır.
          </div>
        </div>
      )}

      {/* Filtre eşleştirme */}
      {ruleType === 'filter_match' && (
        <div style={{ ...s.cardFlat, marginBottom: '16px' }}>
          <SourceCategoryInput form={form} setForm={setForm} />
          <div style={{ marginBottom: '12px' }}>
            <label style={s.labelNormal}>Filtre Grubu</label>
            <p style={{ fontSize: '12px', color: colors.textSecondary, marginBottom: '8px', marginTop: 0 }}>
              Hangi filtre grubuna göre eşleştirme yapılacak? (örn. Yaş, Sınıf)
            </p>
            <select
              value={form.filter_group_code || ''}
              onChange={e => setForm({ ...form, filter_group_code: e.target.value, filter_option_code: '' })}
              style={s.select}>
              <option value="">Filtre Grubu Seç</option>
              {filterGroups.map(g => (
                <option key={g.group_code} value={g.group_code}>{g.group_name} ({g.group_code})</option>
              ))}
            </select>
          </div>

          {filterOptions.length > 0 && (
            <div>
              <label style={s.labelNormal}>
                Filtre Değeri
                <span style={{ color: colors.textMuted, fontSize: '12px', marginLeft: '8px', fontWeight: '400' }}>
                  (opsiyonel — boş bırakılırsa grubun tüm değerleriyle eşleşir)
                </span>
              </label>
              <select
                value={form.filter_option_code || ''}
                onChange={e => setForm({ ...form, filter_option_code: e.target.value })}
                style={s.select}>
                <option value="">Tüm Değerler</option>
                {filterOptions.map(o => (
                  <option key={o.option_code} value={o.option_code}>{o.filter_value} ({o.option_code})</option>
                ))}
              </select>
              {form.filter_option_code && (
                <p style={{ ...ui.numMuted, fontSize: '12px', marginTop: '6px', marginBottom: 0 }}>
                  Seçilen kod: <strong>{form.filter_option_code}</strong>
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: '24px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: colors.textPrimary }}>
          <input type="checkbox" checked={form.match_stock} onChange={e => setForm({ ...form, match_stock: e.target.checked })} style={{ accentColor: colors.primary }} />
          Sadece stokta olanlar
        </label>

        {(ruleType === 'similar' || ruleType === 'price_alternative') && (
          <>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: colors.textPrimary }}>
              <input type="checkbox" checked={form.match_category} onChange={e => setForm({ ...form, match_category: e.target.checked })} style={{ accentColor: colors.primary }} />
              Aynı kategoride eşleştir
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: colors.textPrimary }}>
              <input type="checkbox" checked={form.match_color || false} onChange={e => setForm({ ...form, match_color: e.target.checked })} style={{ accentColor: colors.primary }} />
              Aynı renk eşleştirme
            </label>
          </>
        )}

        {['similar', 'price_alternative', 'brand_match', 'model_match', 'filter_match', 'variant'].includes(ruleType) && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: colors.textPrimary }}>
            <input type="checkbox" checked={form.stock_priority || false} onChange={e => setForm({ ...form, stock_priority: e.target.checked })} style={{ accentColor: colors.primary }} />
            Stok öncelikli sırala
          </label>
        )}

        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: colors.textPrimary }}>
          <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} style={{ accentColor: colors.primary }} />
          Aktif
        </label>
      </div>

      <div className="sx-wrap" style={{ display: 'flex', gap: '24px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {hasPriceRange && (
          <div style={{ minWidth: 0 }}>
            <label style={s.labelNormal}>
              Fiyat Aralığı (%)
              <span style={{ color: colors.textMuted, fontSize: '12px', marginLeft: '8px', fontWeight: '400' }}>(0 = fiyat filtresi yok)</span>
            </label>
            <input type="number" value={form.price_range_percent} onChange={e => setForm({ ...form, price_range_percent: parseInt(e.target.value) })}
              style={{ ...s.input, ...ui.num, width: '100px', maxWidth: '100%' }} />
          </div>
        )}
        {hasLimit && (
          <div style={{ minWidth: 0 }}>
            <label style={s.labelNormal}>Gösterilecek Ürün</label>
            <input type="number" value={form.limit_count} onChange={e => setForm({ ...form, limit_count: parseInt(e.target.value) })}
              style={{ ...s.input, ...ui.num, width: '100px', maxWidth: '100%' }} />
          </div>
        )}
      </div>

      <div className="sx-wrap" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        <button onClick={() => onSave(form)} style={s.btnPrimary}>Kaydet</button>
        <button onClick={onCancel} style={s.btnSecondary}>İptal</button>
      </div>
    </div>
  )
}

export default Segments